import { useMemo } from "react";
import cn from "classnames";
import { CALENDAR_LOCALE } from "@/shared/ui";
import type { Locale } from "@/shared/utils";
import type { CalendarTask } from "./types";
import { toDateKey, isSameDay } from "./calendarUtils";
import CalendarTaskItem from "./CalendarTaskItem";

interface Props {
  currentDate: Date;
  tasksByDate: Map<string, CalendarTask[]>;
  locale: Locale;
  onTaskClick: (task: CalendarTask) => void;
}

export default function CalendarMonthGrid({ currentDate, tasksByDate, locale, onTaskClick }: Props) {
  const weekdays = CALENDAR_LOCALE[locale].weekdays;
  const today = new Date();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const gridDays = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = (firstDay.getDay() + 6) % 7;

    const days: Date[] = [];

    for (let i = startOffset - 1; i >= 0; i--) {
      days.push(new Date(year, month, -i));
    }

    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }

    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        days.push(new Date(year, month + 1, i));
      }
    }

    if (days.length === 35 && startOffset + lastDay.getDate() > 35) {
      let nextDay = days.length - startOffset - lastDay.getDate() + 1;
      while (days.length < 42) {
        days.push(new Date(year, month + 1, nextDay++));
      }
    }

    return days;
  }, [year, month]);

  return (
    <div className="flex-1 flex flex-col">
      <div className="grid grid-cols-7">
        {weekdays.map((w) => (
          <div key={w} className="py-2 text-center text-label-xs content-base-low border-b surface-component-stroke">
            {w}
          </div>
        ))}
      </div>

      <div
        className={cn(
          "grid grid-cols-7 flex-1 border-r border-b surface-component-stroke",
          gridDays.length === 42 ? "grid-rows-6" : "grid-rows-5",
        )}>
        {gridDays.map((day) => {
          const isCurrentMonth = day.getMonth() === month;
          const isToday = isSameDay(day, today);
          const key = toDateKey(day);
          const tasks = tasksByDate.get(key) ?? [];

          return (
            <div
              key={key}
              className={cn(
                "min-h-25 p-1 border-t border-l surface-component-stroke flex flex-col",
                !isCurrentMonth && "surface-tertiary-fill",
              )}>
              <div className="flex justify-end mb-0.5">
                <span
                  className={cn(
                    "text-label-xs w-6 h-6 flex items-center justify-center",
                    isToday && "background-brand-fill text-white radius-full",
                    isCurrentMonth && !isToday && "content-base-primary",
                    !isCurrentMonth && "content-base-low",
                  )}>
                  {day.getDate()}
                </span>
              </div>
              {tasks.length > 0 && (
                <div className="flex-1 flex flex-col gap-0.5 overflow-y-auto component-scroll">
                  {tasks.map((task) => (
                    <CalendarTaskItem key={task.id} task={task} onClick={() => onTaskClick(task)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
